import {
    ComponentType,
    PropsWithChildren,
    useCallback,
    useMemo,
    useState,
} from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TStrings from '../QuestionComponents.i18n';
import SignatureDraw from './SignatureDraw';
import SignatureIcon from '~/components/icons/SignatureIcon';
import TypeIcon from '~/components/icons/TypeIcon';
import UploadIcon from '~/components/icons/UploadIcon';
import SavedIcon from '~/components/icons/SavedIcon';
import type { PanelItemProps, SignatureValue } from './SignaturePanel.types';
import SignatureType from './SignatureType';
import SignatureUpload from './SignatureUpload';
import type { QuestionComponentProps } from '~/lib/Questions/Questions.types';
import { generateUniqueFileName } from './Signature.utils';
import { uploadToS3 } from '~/components/FileUpload';
import Loading from '~/components/Loading';
import BlurViewOverlay from '../BlurViewOverlay';
enum SignatureTab {
    Draw = 'draw',
    Type = 'type',
    Upload = 'upload',
    Saved = 'saved',
}

type SignaturePanelProps = {
    isReadOnly?: boolean;
} & Pick<QuestionComponentProps<SignatureValue>, 'setValue' | 'value'>;

const SignaturePanel = ({
    isReadOnly,
    setValue,
    value,
}: SignaturePanelProps) => {
    const [tabValue, setTabValue] = useState<SignatureTab>(SignatureTab.Draw);
    const [isSavingValue, setIsSavingValue] = useState<boolean>(false);

    const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);

    const onTabClick = useCallback(
        (_: React.SyntheticEvent, selectedTab: SignatureTab) => {
            setTabValue(selectedTab);
        },
        [setTabValue],
    );

    const onSignatureChanged = useCallback(
        async (signature?: string | File | null) => {
            if (signature) {
                let signatureFile: File;
                setIsSavingValue(true);
                if (typeof signature === 'string') {
                    const blob = new Blob([signature], {
                        type: 'image/svg+xml',
                    });

                    const fileName = generateUniqueFileName('svg');
                    signatureFile = new File([blob], fileName, {
                        type: 'image/svg+xml',
                    });
                } else {
                    signatureFile = signature as File;
                }
                const s3file = await uploadToS3(signatureFile as File);
                if (s3file) {
                    setValue(s3file);
                }
                setIsSavingValue(false);
            } else {
                setValue(null);
            }
        },
        [setValue],
    );

    const ActivePanelComponent: ComponentType<
        PropsWithChildren<PanelItemProps>
    > = useMemo(() => {
        switch (tabValue) {
            default:
            case SignatureTab.Draw:
                return SignatureDraw;
            case SignatureTab.Type:
                return SignatureType;
            case SignatureTab.Upload:
                return SignatureUpload;
        }
    }, [tabValue]);

    const isValueSaved = !isReadOnly && !isSavingValue && value;
    const shouldShowAgreementUI = !agreedToTerms && !isReadOnly && !value;
    return (
        <div className="relative">
            <BlurViewOverlay visible={shouldShowAgreementUI}>
                <div className="bg-blue-gray p-6 rounded-2xl space-y-4 w-3/4 ">
                    <div className="text-lg font-semibold text-primary-700">
                        {TStrings.SIGNATURE_TERMS_TITLE}
                    </div>
                    <div className="text-sm text-primary-700">
                        {TStrings.SIGNATURE_TERMS_BODY}
                    </div>
                    <button
                        className="bg-primary-700 text-white py-2 px-6 rounded-full text-sm hover:bg-primary-400"
                        onClick={() => setAgreedToTerms(true)}
                    >
                        {TStrings.SIGNATURE_TERMS_BUTTON}
                    </button>
                </div>
            </BlurViewOverlay>
            <div className="flex grow flex-col">
                <div className="flex flex-row items-center">
                    <Tabs value={tabValue} onChange={onTabClick}>
                        <Tab
                            label={TStrings.SIGNATURE_TAB_DRAW}
                            value={SignatureTab.Draw}
                            icon={<SignatureIcon className="w-6 h-6" />}
                            iconPosition="start"
                        />
                        <Tab
                            label={TStrings.SIGNATURE_TAB_TYPE}
                            value={SignatureTab.Type}
                            icon={<TypeIcon />}
                            iconPosition="start"
                        />
                        <Tab
                            label={TStrings.SIGNATURE_TAB_UPLOAD}
                            value={SignatureTab.Upload}
                            icon={<UploadIcon />}
                            iconPosition="start"
                        />
                    </Tabs>
                    <div className="flex grow justify-end px-2">
                        {isValueSaved ? (
                            <SavedIcon />
                        ) : isSavingValue ? (
                            <Loading />
                        ) : null}
                    </div>
                </div>
                <ActivePanelComponent
                    className="rounded min-w-80 min-h-60 themed-bg-light"
                    readOnly={isReadOnly}
                    onSignatureChanged={onSignatureChanged}
                    isLoading={isSavingValue}
                    hasValue={value != null}
                />
            </div>
        </div>
    );
};
export default SignaturePanel;
